"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.autoAssign = autoAssign;
const openapi_1 = require("@pentacode/openapi");
const model_1 = require("./model");
const time_1 = require("./time");
const util_1 = require("./util");
function getAvailabilitiesForEntry(employeeId, entry, availabilities) {
    return availabilities.filter((availability) => availability.date === entry.date &&
        availability.employeeId === employeeId &&
        entry.matchesTimeFilter({
            from: availability.start,
            to: availability.end,
            fromRule: "open",
            toRule: "open",
        }));
}
function getDynamicAvailability(employee, entry, entries, timeBalance) {
    var _a;
    const log = false;
    const employeeEntries = entries.filter((otherEntry) => {
        return !otherEntry.deleted && otherEntry.employeeId === employee.id;
    });
    // Is not overlapping with another entry
    const overlapping = employeeEntries.find((otherEntry) => otherEntry.overlapsWith(entry));
    if (overlapping) {
        log &&
            console.log("employee is already assigned", overlapping.date, overlapping.startPlanned, overlapping.endPlanned, overlapping);
        return false;
    }
    const shiftDuration = entry.durationPlanned;
    // Max hours per week are 50 or 150% of nominal hours, whichever is lower
    // For helpers, it is the nominal hours
    // TODO: this will not work if range is not 1 week
    const isMarginal = ((_a = employee.getContractForDate(entry.date)) === null || _a === void 0 ? void 0 : _a.employmentType) === model_1.EmploymentType.Marginal;
    const maxWeeklyHours = isMarginal ? timeBalance.nominal : Math.min(50, timeBalance.nominal * 1.5);
    // Is not exceeding the max hours per week
    if (timeBalance.actual + shiftDuration > maxWeeklyHours) {
        log && console.log("employee is exceeding max hours per week");
        return false;
    }
    // Is not exceeding the max hours per day
    const assignedDuration = employeeEntries.reduce((total, otherEntry) => {
        if (otherEntry.date === entry.date) {
            return total + otherEntry.durationPlanned;
        }
        return total;
    }, 0);
    if (assignedDuration + shiftDuration > 10) {
        // TODO: is this always 10 hours?
        log && console.log("employee is exceeding max hours per day");
        return false;
    }
    // Entry is too close to entries on the previous day
    const prevDay = (0, util_1.getPrevDay)(entry.date);
    if (employeeEntries.some((otherEntry) => {
        var _a, _b;
        if (otherEntry.date !== prevDay || !otherEntry.endPlanned || !entry.startPlanned) {
            return false;
        }
        return ((_a = entry.startPlanned) === null || _a === void 0 ? void 0 : _a.getTime()) - ((_b = otherEntry.endPlanned) === null || _b === void 0 ? void 0 : _b.getTime()) < 11 * time_1.hour;
    })) {
        log && console.log("employee has too little rest time");
        return false;
    }
    // Entry is too close to entries on the next day
    const nextDay = (0, util_1.getNextDay)(entry.date);
    if (employeeEntries.some((otherEntry) => {
        var _a, _b;
        if (otherEntry.date !== nextDay || !entry.endPlanned || !otherEntry.startPlanned) {
            return false;
        }
        return ((_a = otherEntry.startPlanned) === null || _a === void 0 ? void 0 : _a.getTime()) - ((_b = entry.endPlanned) === null || _b === void 0 ? void 0 : _b.getTime()) < 11 * time_1.hour;
    })) {
        log && console.log("employee has too little rest time");
        return false;
    }
    return true;
}
function isEmployeeAvailable({ employee, timeBalance }, entry, { entries, absences, departments, availabilities }) {
    var _a, _b;
    const log = false;
    log &&
        console.log("checking employee ", employee.name, (_a = entry.position) === null || _a === void 0 ? void 0 : _a.name, entry.date, entry.startPlanned, entry.endPlanned, entry);
    // Has any positions of department
    if (departments && !employee.positions.some((position) => departments === null || departments === void 0 ? void 0 : departments.includes(position.departmentId))) {
        log && console.log("employee is not working in this department");
        return false;
    }
    // Is active
    if (employee.status !== model_1.EmployeeStatus.Active) {
        log && console.log("employee is not active");
        return false;
    }
    // Has position of entry
    if (!employee.positions.some((position) => entry.positionId === position.id)) {
        log && console.log("employee is not qualified");
        return false;
    }
    // Entry has valid date
    const date = (0, util_1.parseDateString)(entry.date);
    if (!date) {
        log && console.log("entry has invalid date");
        return false;
    }
    // Has contract
    const contract = employee.getContractForDate(entry.date);
    if (!contract || contract.blocked === true) {
        log && console.log("employee does not have a valid contract");
        return false;
    }
    // is within contractual workdays
    if (contract.fixedWorkDays) {
        const hoursPerDayIdx = (date.getDay() + 6) % 7; // date is Sun - Sat, hoursPerDay is Mon - Sun
        const hours = ((_b = contract.hoursPerDay) === null || _b === void 0 ? void 0 : _b[hoursPerDayIdx]) || 0;
        if (hours === 0) {
            log && console.log("employee is not working on this day");
            return false;
        }
    }
    // Employee is available
    if (getAvailabilitiesForEntry(employee.id, entry, availabilities).some((availability) => availability.status === model_1.AvailabilityStatus.Unavailable)) {
        log && console.log("employee is unavailable");
        return false;
    }
    if (absences.some((absence) => absence.employeeId === employee.id &&
        [model_1.AbsenceStatus.Approved, model_1.AbsenceStatus.Inferred].includes(absence.status) &&
        absence.start <= entry.date &&
        absence.end > entry.date)) {
        log && console.log("employee is absent");
        return false;
    }
    const freeDays = entries.filter((otherEntry) => !otherEntry.deleted && otherEntry.employeeId === employee.id && otherEntry.type === model_1.TimeEntryType.Free);
    if (freeDays.some((freeDay) => freeDay.date === entry.date)) {
        log && console.log("employee is on free day");
        return false;
    }
    if (!getDynamicAvailability(employee, entry, entries, timeBalance)) {
        return false;
    }
    return true;
}
function getEmployeeScore(entry, empData, context) {
    const scores = {
        reduceAccumulatedOvertime: 0,
        matchNominalHours: 0,
        considerAvailabilities: 0,
    };
    // it matters to include the current entry in the time balance calculation.
    // This is because there might be different contracts between employees,
    // resulting in a different score for the same duration.
    const timeBalanceAfterShift = empData.timeBalance.actual + entry.durationPlanned;
    // score if the employee has deviating balance carry
    // At the start, this will be lower than the minimum.
    // It will only become a relevant criteria when the employee has less than 50% nominal minus hours
    // The more plus hours the employee has, the less incentivized they are to take on more work
    // Employees with more than 50% nominal surplus will rarely be assigned until their balance has adjusted or other employees match their balance.
    const maxOvertimeDeviation = 0.5 * empData.timeBalance.nominal; // has to work between 50% and 150% of the nominal hours
    const finalBalance = empData.timeBalance.balance + timeBalanceAfterShift; // balance after the time range and all current assignments, including the new entry
    const overtimeClamped = (0, util_1.clamp)(finalBalance, -maxOvertimeDeviation, maxOvertimeDeviation);
    const overtimeFraction = (overtimeClamped + maxOvertimeDeviation) / (2 * maxOvertimeDeviation); // remap deviation to 0-1 range
    scores.reduceAccumulatedOvertime = 1 - overtimeFraction; // invert so negative balance is highest score
    // score for matching the nominal hours
    // we need this in addition to `reduceAccumulatedOvertime` in case two employees have a huge time deficit/surplus, where
    // we still need to make sure available shifts are equally distributed between them.
    const actualFraction = timeBalanceAfterShift / empData.timeBalance.nominal; // can be from 0 to 1.5
    const clampedFraction = (0, util_1.clamp)(actualFraction - 0.5, 0, 1); // look at 0.5 to 1.5, below 0.5 will always be highest score
    scores.matchNominalHours = 1 - clampedFraction; // invert for 0 to be max score, 1 to be min score
    // score for availabilities
    // TODO: maybe we could calculate this based on how much of the shift is prefered
    // TODO: map this to a range of 0-1
    const availabilites = getAvailabilitiesForEntry(empData.employee.id, entry, context.availabilities);
    scores.considerAvailabilities = availabilites.reduce((total, av) => {
        switch (av.status) {
            case model_1.AvailabilityStatus.Unpreferred:
                return total - 1;
            case model_1.AvailabilityStatus.Available:
                return total + 1;
            case model_1.AvailabilityStatus.Preferred:
                return total + 2;
            default:
                return total;
        }
    }, 0);
    return scores;
}
// get min/max for a score
function getBoundsForScore(key, scores) {
    let min = 0;
    let max = 0;
    for (const score of scores.values()) {
        const value = score[key];
        min = Math.min(value, min);
        max = Math.max(value, max);
    }
    return [min, max];
}
function normalizeScores(scores) {
    const normalized = new Map();
    const [availabilitesMin, availabilitesMax] = getBoundsForScore("considerAvailabilities", scores);
    const availabilitiesDelta = availabilitesMax - availabilitesMin;
    for (const [employeeId, score] of scores.entries()) {
        // remap score to 0-1 range
        const considerAvailabilities = availabilitiesDelta === 0 ? 0 : (score.considerAvailabilities - availabilitesMin) / availabilitiesDelta;
        const normalizedScore = {
            reduceAccumulatedOvertime: score.reduceAccumulatedOvertime,
            matchNominalHours: score.matchNominalHours,
            considerAvailabilities,
        };
        normalized.set(employeeId, normalizedScore);
    }
    return normalized;
}
// sums up scores based on the weights
function getFinalScores(scores, weights) {
    const final = new Map();
    for (const [employeeId, score] of scores.entries()) {
        final.set(employeeId, Object.entries(score).reduce((total, [key, value]) => total + value * weights[key], 0));
    }
    return final;
}
function findEmployee(entry, available, context) {
    // score every employee
    const scores = available.reduce((all, emp) => all.set(emp.employee.id, getEmployeeScore(entry, emp, context)), new Map());
    const normalized = normalizeScores(scores);
    const finalScores = getFinalScores(normalized, context.weights);
    // return the employee with the best score
    available.sort((a, b) => finalScores.get(b.employee.id) - finalScores.get(a.employee.id));
    return available[0];
}
function autoAssign(context) {
    // take only relevant entries (work, not final, not deleted, in time range, department)
    const entries = context.entries
        .filter((entry) => {
        var _a, _b;
        return !entry.deleted &&
            entry.type === model_1.TimeEntryType.Work &&
            entry.startPlanned &&
            entry.endPlanned &&
            // TODO: this might be necessary, however it might be better to apply all unassigned entries as not final
            // maybe it makes more sense to ignore all entries that have a logged time?
            // !entry.startFinal && !entry.endFinal &&
            (!context.departments ||
                !((_a = entry.position) === null || _a === void 0 ? void 0 : _a.departmentId) ||
                context.departments.includes((_b = entry.position) === null || _b === void 0 ? void 0 : _b.departmentId)) &&
            entry.date >= context.range.from &&
            entry.date < context.range.to &&
            (!context.time || entry.matchesTimeFilter(context.time));
    })
        .sort((a, b) => b.duration - a.duration);
    if (context.reassignEntries) {
        // set all employeeIds to null and reset time balance
        entries.forEach((e) => (e.employeeId = null));
        context.employees.forEach((e) => (e.timeBalance.actual = 0));
    }
    // filter entries that are unassigned (all in case of `reassignEntries`)
    const unassigned = entries.filter((e) => !e.employeeId);
    // create maps for available employees to time entries
    const availableEmployees = new Map();
    const availableEntries = new Map();
    for (const entry of unassigned) {
        const availableEmployeeData = context.employees.filter((e) => {
            return isEmployeeAvailable(e, entry, context);
        });
        availableEmployees.set(entry.id, availableEmployeeData);
        for (const employeeData of availableEmployeeData) {
            if (!availableEntries.has(employeeData.employee.id)) {
                availableEntries.set(employeeData.employee.id, []);
            }
            availableEntries.get(employeeData.employee.id).push(entry);
        }
    }
    while (unassigned.length) {
        // get entry with the least available employees
        // TODO: maybe we can improve the selection of the next entry.
        // if availableEmployees are the same length, we can still sort by duration like above
        // we can also try and assign earlier entries first. These entries would be assigned to the employee in most need of hours,
        // so we leave the employee open to get assigned a second later shift.
        unassigned.sort((a, b) => availableEmployees.get(a.id).length - availableEmployees.get(b.id).length);
        const entry = unassigned.shift();
        // find the best suited employee for this entry
        const employeeData = findEmployee(entry, availableEmployees.get(entry.id), context);
        if (employeeData) {
            // assign the employee, update time balance
            entry.employeeId = employeeData.employee.id;
            employeeData.timeBalance.actual = (0, openapi_1.add)(employeeData.timeBalance.actual, entry.duration);
            // update available entries to remove this entry and any that would be in conflict with it
            const employeeAvailableEntries = availableEntries.get(employeeData.employee.id);
            // update available entries
            const newAvailableEntries = employeeAvailableEntries.filter((otherEntry) => getDynamicAvailability(employeeData.employee, otherEntry, entries, employeeData.timeBalance));
            availableEntries.set(employeeData.employee.id, newAvailableEntries);
            // update available employees for each removed entry
            employeeAvailableEntries
                .filter((otherEntry) => !newAvailableEntries.includes(otherEntry)) // only removed entries
                .forEach((otherEntry) => {
                const entryAvailableEmployees = availableEmployees.get(otherEntry.id);
                const newAvailableEmployees = entryAvailableEmployees === null || entryAvailableEmployees === void 0 ? void 0 : entryAvailableEmployees.filter((otherEmployeeData) => otherEmployeeData.employee.id !== employeeData.employee.id);
                newAvailableEmployees && availableEmployees.set(otherEntry.id, newAvailableEmployees);
            });
        }
    }
}
// THOUGHTS:
// Hierarchical weights still allow some override by lower weights
// for example, 0.1 score difference for `reduceAccumulatedOvertime` would match a 1.0 score difference for `matchNominalHours`
// for a 40h contract that would be a balance delta of 2h would equal a current workload of 20h or less
// the same, working 3h less (beyond the initial 50%) would equal having a full "preferred" availability
//# sourceMappingURL=auto-assign.js.map